/**
 * The rail's project filter — a labeled select over a searchable list, built
 * for workspaces with hundreds of projects.
 *
 * LED-1151 (`home-projects.dc.html`) restyles the TRIGGER as the nav header's
 * white scope box — the scope's dot, its name, a caret; the dot alone on the
 * 68px rail — and adds a PINNED group above Recent (hover a row to pin it).
 * The paged, server-searched list below is unchanged.
 *
 * Design: `Led AI - Project Picker.dc.html` (Claude Design project
 * `806e74fa-5b1c-4c5c-bd8e-4d71ff80a106`), frames 1a and 1c. The trigger
 * reads as a field — a small PROJECT eyebrow, the current name, a chevron —
 * and wears a gold ring while open. The panel opens with the cursor in a
 * search field; All projects stays pinned on top, then Recent, then the rest
 * alphabetically.
 * Typing replaces the sections with one match list, matched text bolded.
 *
 * ## The browser never holds the whole list
 *
 * Rows come from `GET /api/project-picker`, one page at a time (see
 * `useProjectPicker`). Search runs on the server over the whole workspace;
 * the next page is asked for when the reader scrolls — or arrows — near the
 * end. A workspace of a thousand projects opens with forty rows on the wire.
 *
 * ## Selection changes the URL, never local state
 *
 * Choosing a project rewrites `?site=` and lets every page re-query, so the
 * filter follows the reader across pages, survives a reload and a pasted
 * link, and the SERVER decides what a filtered page contains.
 *
 * ## Why the panel is portalled
 *
 * The rail is `overflow: hidden` and its nav is `overflow: auto`; the panel
 * (352px) is wider than the rail it opens from. `AnchoredPanel` escapes both
 * and keeps the panel on screen.
 */

import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ConsoleSite } from "../api";
import { DS, FONT_MONO, H } from "../tokens";
import { PROCORE_MARK_URL } from "../assets/procoreMark";
import { createPortal } from "react-dom";
import { label as pickerLabel, pickerRows, usePins, type PickerProject as HomePickerProject, type PickerSort } from "./projectPins";
import { AnchoredPanel } from "../components/AnchoredPanel";
import { SourceMark } from "../views/ProjectDetails";
import { splitMatch, type PickerProject } from "../../server/queries/projectPicker";
import {
  rememberRecentProject,
  useProjectPicker,
  workspaceKeyOf,
  type ProjectPickerDeps,
  type ProjectPickerState,
} from "./useProjectPicker";

/** How close to the end of the list (px) the next page is asked for. */
const LOAD_AHEAD_PX = 240;
const PANEL_WIDTH = 352;

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : undefined, transition: "transform 0.14s" }}
      aria-hidden
    >
      <path d="M7 10l5 5 5-5" stroke={DS.meta} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={DS.ink} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0 }} aria-hidden>
      <circle cx="8" cy="8" r="5.4" stroke={DS.meta} strokeWidth="1.7" />
      <path d="M12 12l4 4" stroke={DS.meta} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

// ── The list model ────────────────────────────────────────────────────────

export interface PickerSection {
  key: string;
  title: string;
  meta: string;
  rows: PickerProject[];
}

/** One keyboard stop: `null` is "All projects". */
export interface PickerStop {
  key: string;
  siteId: string | null;
}

/**
 * Sections and keyboard order for what is loaded so far. Pure, so the
 * grouping rule has a test of its own.
 */
export function buildPickerModel(
  state: ProjectPickerState,
  /** LED-1151: the reader's pinned projects, already named from the shell's
   *  sites. They lead the list and are left out of Recent and All below. */
  pinned: PickerProject[] = [],
): { sections: PickerSection[]; stops: PickerStop[]; searching: boolean } {
  const searching = state.settledQuery !== "";
  const sections: PickerSection[] = [];
  const pinnedIds = new Set(pinned.map((row) => row.id));
  if (searching) {
    if (state.rows.length > 0) {
      sections.push({
        key: "match",
        title: `${state.matchCount} ${state.matchCount === 1 ? "MATCH" : "MATCHES"}`,
        meta: `OF ${state.total}`,
        rows: state.rows,
      });
    }
  } else {
    if (pinned.length > 0) sections.push({ key: "pinned", title: "PINNED", meta: "", rows: pinned });
    const recent = state.recent.filter((row) => !pinnedIds.has(row.id));
    if (recent.length > 0) sections.push({ key: "recent", title: "RECENT", meta: "", rows: recent });
    // One alphabetical run, no letter headers, without what Recent already
    // shows. It only needs a title to set it apart from Recent above it.
    const recentIds = new Set(recent.map((row) => row.id));
    const rest = state.rows.filter((row) => !recentIds.has(row.id) && !pinnedIds.has(row.id));
    if (rest.length > 0) sections.push({ key: "az", title: sections.length > 0 ? "ALL" : "", meta: "", rows: rest });
  }
  const stops: PickerStop[] = searching ? [] : [{ key: "all", siteId: null }];
  for (const section of sections) for (const row of section.rows) stops.push({ key: `${section.key}:${row.id}`, siteId: row.id });
  return { sections, stops, searching };
}

// ── The component ─────────────────────────────────────────────────────────

export function ProjectPicker({
  sites,
  activeSiteId,
  onSelect,
  search,
  open,
  onOpenChange,
  deps,
  rail,
}: {
  /** The 68px icon rail: the trigger folds to the scope dot alone. */
  rail?: boolean;
  /** The workspace's sites as the shell already holds them — used only to
   *  NAME the current scope on the trigger, never to fill the list. */
  sites: ConsoleSite[];
  /** The current `?site=`, or null for all projects. */
  activeSiteId: string | null;
  /** `null` clears the filter. */
  onSelect: (siteId: string | null) => void;
  /** The page's query string; the picker reads its `workspace`. */
  search: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Test seam for the data layer. */
  deps?: ProjectPickerDeps;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const { state, setQuery, loadMore, retry } = useProjectPicker(search, open, deps);
  const [pins, togglePin] = usePins();
  const pinnedRows = useMemo(
    () => pins
      .map((id) => sites.find((site) => site.id === id))
      .filter((site): site is ConsoleSite => !!site)
      .map((site) => ({ id: site.id, name: site.name, walks: 0 })),
    [pins, sites],
  );
  const { sections, stops, searching } = useMemo(() => buildPickerModel(state, pinnedRows), [state, pinnedRows]);
  const [cursor, setCursor] = useState(0);
  const workspaceKey = workspaceKeyOf(search);

  // Recent is "the last few you opened" — however they were opened.
  useEffect(() => {
    if (activeSiteId) rememberRecentProject(workspaceKey, activeSiteId);
  }, [activeSiteId, workspaceKey]);

  // A new answer starts the keyboard at the top of it.
  useEffect(() => setCursor(0), [state.settledQuery, open]);

  // Search-first: the cursor lands in the field. One frame late on purpose —
  // AnchoredPanel keeps the panel `visibility: hidden` until it has measured
  // where it goes, and a hidden field cannot take focus.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => input.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Keep the keyboard row in view.
  const activeKey = stops[Math.min(cursor, stops.length - 1)]?.key;
  useEffect(() => {
    if (!open || !activeKey) return;
    const row = list.current?.querySelector<HTMLElement>(`[data-key="${activeKey}"]`);
    row?.scrollIntoView?.({ block: "nearest" });
  }, [activeKey, open]);

  // Fill: a page shorter than the panel shows no scrollbar to scroll, so ask
  // for the next one straight away. (Skipped without layout — clientHeight 0.)
  useEffect(() => {
    const el = list.current;
    if (!open || !el || el.clientHeight === 0) return;
    if (state.hasMore && !state.loadingMore && !state.moreFailed && el.scrollHeight <= el.clientHeight + LOAD_AHEAD_PX) loadMore();
  }, [open, state.rows, state.hasMore, state.loadingMore, state.moreFailed, loadMore]);

  const onScroll = () => {
    const el = list.current;
    if (!el || !state.hasMore || state.loadingMore || state.moreFailed) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_AHEAD_PX) loadMore();
  };

  const pick = (siteId: string | null) => {
    onOpenChange(false);
    if (siteId) rememberRecentProject(workspaceKey, siteId);
    onSelect(siteId);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = Math.min(cursor + 1, stops.length - 1);
      setCursor(next);
      // Arrowing toward the end asks for more, as scrolling would.
      if (stops.length - next <= 5 && state.hasMore && !state.loadingMore) loadMore();
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setCursor(Math.max(0, cursor - 1));
    } else if (event.key === "Enter") {
      const stop = stops[cursor];
      if (stop) {
        event.preventDefault();
        pick(stop.siteId);
      }
    }
  };

  const active = sites.find((site) => site.id === activeSiteId);
  // Which system each project syncs to, for its mark (the project-scoped
  // nav). The picker's own rows do not carry it; the shell's sites do.
  const sourceById = useMemo(() => new Map(sites.map((site) => [site.id, site.syncSource])), [sites]);
  const current = active ? active.name || active.id : "All projects";
  const highlight = state.settledQuery;

  return (
    <>
      <button
        ref={trigger}
        type="button"
        onClick={() => onOpenChange(!open)}
        onMouseEnter={rail ? pinTipOf : undefined}
        data-testid="project-picker"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Project: ${current}`}
        className={rail ? "kb navpick railrow" : "kb navpick"}
        data-tip={rail ? current : undefined}
        title={rail ? undefined : "Filter every list below by project"}
        style={{
          // LED-1151: the nav header's white scope box — dot, name, caret —
          // folding to the dot alone on the 68px rail.
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: rail ? "center" : "flex-start",
          gap: 8,
          width: "100%",
          height: 38,
          padding: rail ? 0 : "0 10px",
          borderRadius: 9,
          background: H.white,
          border: `1px solid ${open ? H.ink : H.edge}`,
          cursor: "pointer",
          fontFamily: "inherit",
          textAlign: "left",
          color: H.ink,
        }}
      >
        <span
          data-testid="project-picker-dot"
          style={{ flex: "none", width: 8, height: 8, borderRadius: 4, background: active ? H.scopeDot : H.dotIdle }}
        />
        {!rail && (
          <>
            <span
              data-testid="project-picker-current"
              style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {current}
            </span>
            <Chevron open={open} />
          </>
        )}
      </button>

      {open && (
        <AnchoredPanel
          anchorRef={trigger}
          onClose={() => onOpenChange(false)}
          label="Switch project"
          role="dialog"
          align="left"
          minWidth={PANEL_WIDTH}
          maxWidth={PANEL_WIDTH}
          testId="project-picker-menu"
          panelStyle={{
            width: PANEL_WIDTH,
            padding: 0,
            // The design's white, not AnchoredPanel's cream: the sticky
            // letter headers are white, and on cream they read as bands.
            background: DS.surface,
            border: "none",
            borderRadius: 18,
            boxShadow: "0 18px 44px rgba(10,10,10,0.16), 0 0 0 1px rgba(10,10,10,0.06)",
            display: "flex",
            flexDirection: "column",
            maxHeight: "min(470px, calc(100vh - 160px))",
            overflowY: "hidden",
          }}
        >
          <div style={{ flex: "none", padding: "10px 10px 6px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, height: 40, padding: "0 12px", borderRadius: 12, background: DS.paper }}>
              <SearchIcon />
              <input
                ref={input}
                data-testid="project-picker-search"
                type="text"
                role="combobox"
                aria-expanded
                aria-controls="project-picker-list"
                aria-activedescendant={activeKey ? `pick-${activeKey}` : undefined}
                aria-label="Search projects"
                value={state.query}
                onChange={(event) => setQuery(event.target.value)}
                // Mirrored on onInput, as TimeField does: happy-dom delivers
                // "input" without React's onChange, and a browser fires both.
                onInput={(event) => setQuery((event.target as HTMLInputElement).value)}
                onKeyDown={onKeyDown}
                placeholder={state.total > 0 ? `Search ${state.total} projects` : "Search projects"}
                style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: "inherit", fontSize: 14.5, color: DS.ink }}
              />
              {state.query && (
                <button
                  type="button"
                  data-testid="project-picker-clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setQuery("");
                    input.current?.focus();
                  }}
                  style={{ width: 18, height: 18, borderRadius: 9, border: "none", padding: 0, background: DS.faint, color: DS.surface, fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {!searching && (
            <>
              <button
                type="button"
                role="option"
                id="pick-all"
                data-key="all"
                data-testid="project-picker-all"
                aria-selected={!activeSiteId}
                data-active={activeKey === "all"}
                className="pickrow"
                onClick={() => pick(null)}
                onMouseMove={() => setCursor(0)}
                style={{
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  height: 44,
                  margin: "0 10px",
                  padding: "0 12px",
                  borderRadius: 12,
                  border: "none",
                  font: "inherit",
                  color: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  ...(!activeSiteId ? { background: DS.paper } : {}),
                }}
              >
                <span style={{ flex: 1, fontSize: 15.5, fontWeight: 800, letterSpacing: "-0.3px" }}>All projects</span>
                {!activeSiteId && <CheckIcon />}
              </button>
              <div style={{ flex: "none", height: 1, background: DS.line, margin: "6px 18px 0" }} />
            </>
          )}

          <div
            ref={list}
            id="project-picker-list"
            role="listbox"
            aria-label="Projects"
            data-testid="project-picker-list"
            className="noscroll"
            onScroll={onScroll}
            // No top padding: a sticky header pins to the scroller's top
            // edge, and padding above it shows a sliver of the row scrolling
            // out. The 4px the design puts there is a spacer that scrolls away.
            style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 10px 10px" }}
          >
            <div style={{ height: 4 }} />
            {sections.map((section) => (
              <Fragment key={section.key}>
                {section.title && <div
                  data-testid="project-picker-header"
                  style={{ position: "sticky", top: 0, zIndex: 1, background: DS.surface, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px 4px" }}
                >
                  <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "1.4px", color: DS.meta }}>{section.title}</span>
                  <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: DS.faint }}>{section.meta}</span>
                </div>}
                {section.rows.map((row) => {
                  const key = `${section.key}:${row.id}`;
                  const on = row.id === activeSiteId;
                  const parts = splitMatch(row.name || row.id, highlight);
                  return (
                    <button
                      key={key}
                      type="button"
                      role="option"
                      id={`pick-${key}`}
                      data-key={key}
                      data-testid="project-picker-option"
                      data-site-id={row.id}
                      aria-selected={on}
                      data-active={activeKey === key}
                      className="pickrow pc"
                      onClick={() => pick(row.id)}
                      onMouseMove={() => {
                        const index = stops.findIndex((stop) => stop.key === key);
                        if (index !== cursor) setCursor(index);
                      }}
                      title={row.name || row.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        height: 40,
                        padding: "0 12px",
                        borderRadius: 10,
                        border: "none",
                        font: "inherit",
                        color: "inherit",
                        textAlign: "left",
                        cursor: "pointer",
                        scrollMarginTop: 30,
                        ...(on ? { background: DS.paper } : {}),
                      }}
                    >
                      <SourceMark source={sourceById.get(row.id)} size={14} />
                      <span data-testid="project-picker-option-name" style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: on ? 800 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {parts.pre}
                        {parts.hit && <span style={{ fontWeight: 800, background: DS.bandCp, borderRadius: 3 }}>{parts.hit}</span>}
                        {parts.post}
                      </span>
                      <span
                        role="button"
                        tabIndex={-1}
                        className="pcShare"
                        data-testid="project-pin"
                        data-pinned={pins.includes(row.id) ? "true" : "false"}
                        aria-label={pins.includes(row.id) ? `Unpin ${row.name || row.id}` : `Pin ${row.name || row.id}`}
                        title={pins.includes(row.id) ? "Unpin" : "Pin to the top"}
                        // Focus stays in the search field: pinning is a side action.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                          event.stopPropagation();
                          togglePin(row.id);
                        }}
                        style={{ flex: "none", width: 24, height: 24, borderRadius: 12, display: "inline-flex", alignItems: "center", justifyContent: "center", ...(pins.includes(row.id) ? { opacity: 1 } : {}) }}
                      >
                        <Glyph d={ICON.pin} size={12} color={pins.includes(row.id) ? H.goldInk : H.muted} />
                      </span>
                      {on && <CheckIcon />}
                    </button>
                  );
                })}
              </Fragment>
            ))}

            {state.status === "loading" && state.rows.length === 0 && (
              <div data-testid="project-picker-loading" style={{ padding: "30px 12px", textAlign: "center", fontSize: 14, color: DS.meta }}>
                Loading projects…
              </div>
            )}
            {state.status === "error" && (
              <div data-testid="project-picker-error" style={{ padding: "30px 12px", textAlign: "center", fontSize: 14, color: DS.meta }}>
                Couldn’t load projects.{" "}
                <button type="button" onClick={retry} style={{ border: "none", background: "none", padding: 0, font: "inherit", color: DS.accentInk, cursor: "pointer" }}>
                  Retry
                </button>
              </div>
            )}
            {state.status === "ready" && searching && state.matchCount === 0 && (
              <div data-testid="project-picker-empty" style={{ padding: "30px 12px", textAlign: "center", fontSize: 14, color: DS.meta }}>
                No projects match “{state.settledQuery}”
              </div>
            )}
            {state.status === "ready" && !searching && state.total === 0 && (
              <div data-testid="project-picker-empty" style={{ padding: "30px 12px", textAlign: "center", fontSize: 14, color: DS.meta }}>
                No projects in this workspace yet
              </div>
            )}
            {state.loadingMore && (
              <div data-testid="project-picker-more" style={{ padding: "10px 12px", fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: "1px", color: DS.dashed }}>
                LOADING MORE…
              </div>
            )}
            {state.moreFailed && (
              <div data-testid="project-picker-more-failed" style={{ padding: "10px 12px", fontSize: 13, color: DS.meta }}>
                Couldn’t load more.{" "}
                <button type="button" onClick={loadMore} style={{ border: "none", background: "none", padding: 0, font: "inherit", color: DS.accentInk, cursor: "pointer" }}>
                  Retry
                </button>
              </div>
            )}
          </div>

          <div style={{ flex: "none", borderTop: `1px solid ${DS.line}`, padding: "8px 12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span data-testid="project-picker-footer" style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 600, letterSpacing: "1px", color: DS.dashed }}>
              {!searching && state.status === "ready" ? `${state.total} PROJECTS · ${state.recent.length} RECENT` : ""}
            </span>
            <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: DS.faint }}>↑↓ · ENTER</span>
          </div>
        </AnchoredPanel>
      )}
    </>
  );
}

// ── Home's "Find a project" (LED-1151) ─────────────────────────────────────

const MONO_HEAD: React.CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 8.5,
  fontWeight: 700,
  letterSpacing: "1.3px",
  color: H.faint,
};

function Glyph({ d, size = 14, color = H.sub, width = 1.8 }: { d: string; size?: number; color?: string; width?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flexShrink: 0, display: "block" }}>
      {d.split("|").map((part) => <path key={part} d={part} />)}
    </svg>
  );
}

export const ICON = {
  search: "M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13Z|m20 20-4.8-4.8",
  list: "M4 6h16|M4 12h16|M4 18h16",
  check: "M20 6 9 17l-5-5",
  caret: "m6 9 6 6 6-6",
  pin: "M12 21s6.5-6 6.5-10.5a6.5 6.5 0 1 0-13 0C5.5 15 12 21 12 21Z|M12 12.6a2.2 2.2 0 1 0 0-4.4 2.2 2.2 0 0 0 0 4.4Z",
  plus: "M12 5v14|M5 12h14",
};

/** Pin the rail's fly-out label beside the row (see `.railrow` in main.tsx). */
function pinTipOf(event: { currentTarget: HTMLElement }): void {
  const box = event.currentTarget.getBoundingClientRect();
  event.currentTarget.style.setProperty("--tip-y", `${box.top + box.height / 2}px`);
}

/** The dot a project row leads with: amber when it has work waiting. */
function rowDot(project: HomePickerProject, on: boolean): string {
  if (project.toReview > 0) return H.amberDot;
  return on ? H.ink : H.dotIdle;
}

function lastWalkLabel(project: HomePickerProject): string {
  if (!project.lastWalkAt) return "No walks yet";
  const at = new Date(project.lastWalkAt);
  return `Last walk ${at.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

/**
 * Keyboard for a picker's input. `flat` is the list in keyboard order.
 */
export function pickerKey(
  event: React.KeyboardEvent,
  flat: HomePickerProject[],
  hi: number,
  setHi: (next: number) => void,
  pick: (siteId: string) => void,
  close: () => void,
): void {
  if (event.key === "Escape") {
    event.preventDefault();
    close();
  } else if (event.key === "ArrowDown") {
    event.preventDefault();
    setHi(Math.min(flat.length - 1, hi + 1));
  } else if (event.key === "ArrowUp") {
    event.preventDefault();
    setHi(Math.max(0, hi - 1));
  } else if (event.key === "Enter" && flat[hi]) {
    event.preventDefault();
    pick(flat[hi]!.id);
  }
}

/**
 * The scrolling list both pickers share. `detailed` adds the Home search's
 * review badge, last-walk meta, integration mark and hover-to-pin.
 */
function ProjectRows({
  projects,
  query,
  sort,
  hi,
  setHi,
  activeSiteId,
  onPick,
  onAll,
  detailed,
  maxHeight,
}: {
  projects: HomePickerProject[];
  query: string;
  sort: PickerSort;
  hi: number;
  setHi: (index: number) => void;
  activeSiteId: string | null;
  onPick: (siteId: string) => void;
  /** Present when the list leads with an "All projects" row. */
  onAll?: () => void;
  detailed?: boolean;
  maxHeight: number;
}) {
  const [pins, togglePin] = usePins();
  const { rows } = pickerRows(projects, pins, query, sort);
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the keyboard highlight in view in a long list.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${hi}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [hi]);

  return (
    <div ref={listRef} className="noscroll" style={{ maxHeight, overflowY: "auto", padding: "4px 6px 6px" }}>
      {onAll && !query.trim() && (
        <button
          type="button"
          role="menuitemradio"
          aria-checked={!activeSiteId}
          data-testid="project-picker-all"
          className="kb pickrow"
          onClick={onAll}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            width: "100%",
            height: 42,
            padding: "0 10px",
            border: 0,
            borderRadius: 9,
            font: "inherit",
            color: H.ink,
            cursor: "pointer",
            textAlign: "left",
            ...(activeSiteId ? {} : { background: H.pickHover }),
          }}
        >
          <Glyph d={ICON.list} color={H.sub} />
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>All projects</span>
          <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: H.faint }}>{projects.length}</span>
          {!activeSiteId && <Glyph d={ICON.check} size={13} color={H.ink} width={2} />}
        </button>
      )}
      {rows.map((row) => {
        if (row.type === "head") {
          return (
            <div key={`h-${row.label}`} style={{ ...MONO_HEAD, padding: "10px 10px 4px" }}>
              {row.label}
            </div>
          );
        }
        const { project, index, pinned } = row;
        const on = project.id === activeSiteId;
        return (
          <div
            key={project.id}
            role="menuitemradio"
            aria-checked={on}
            tabIndex={-1}
            data-testid="project-picker-option"
            data-site-id={project.id}
            data-index={index}
            data-active={index === hi ? "true" : undefined}
            className="pickrow pc"
            onMouseEnter={() => setHi(index)}
            onClick={() => onPick(project.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              height: detailed ? 44 : 42,
              padding: "0 10px",
              borderRadius: 9,
              cursor: "pointer",
              // Only the keyboard highlight and the chosen project pin a
              // ground inline; a resting row leaves it to `.pickrow:hover`,
              // which an inline "transparent" would outrank.
              ...(index === hi ? { background: H.pickHi } : on ? { background: H.pickHover } : {}),
            }}
          >
            <span style={{ flex: "none", width: 8, height: 8, borderRadius: 4, background: rowDot(project, on) }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {pickerLabel(project)}
            </span>
            {detailed && project.toReview > 0 && (
              <span style={{ ...MONO_HEAD, letterSpacing: "1px", color: H.amberInk, whiteSpace: "nowrap" }}>
                {project.toReview} TO REVIEW
              </span>
            )}
            {detailed && (
              <span style={{ flex: "none", width: 130, fontSize: 12.5, color: H.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {lastWalkLabel(project)}
              </span>
            )}
            {detailed && (
              <span style={{ flex: "none", width: 16, display: "inline-flex", justifyContent: "center" }}>
                {project.syncSource === "procore" && (
                  <img src={PROCORE_MARK_URL} alt="Procore" width={14} height={14} style={{ borderRadius: 3, display: "block" }} />
                )}
                {project.syncSource === "autodesk" && (
                  <span title="Autodesk" style={{ fontFamily: FONT_MONO, fontSize: 8, fontWeight: 700, color: H.sub }}>A</span>
                )}
              </span>
            )}
            <button
              type="button"
              className="kb pcShare"
              aria-label={pinned ? `Unpin ${pickerLabel(project)}` : `Pin ${pickerLabel(project)}`}
              title={pinned ? "Unpin" : "Pin to the top"}
              data-testid="project-pin"
              data-pinned={pinned ? "true" : "false"}
              // Keep focus in the search field: pinning is a side action, and
              // the reader's next keystroke still belongs to the query.
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation();
                togglePin(project.id);
              }}
              style={{
                flex: "none",
                width: 26,
                height: 26,
                borderRadius: 13,
                border: 0,
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                cursor: "pointer",
                opacity: pinned ? 1 : undefined,
              }}
            >
              <Glyph d={ICON.pin} size={12} color={pinned ? H.goldInk : H.muted} />
            </button>
            {on && <Glyph d={ICON.check} size={13} color={H.ink} width={2} />}
          </div>
        );
      })}
      {rows.length === 0 && (
        <div style={{ padding: 22, textAlign: "center", fontSize: 13, color: H.muted }}>
          {`No projects match “${query.trim()}”`}
        </div>
      )}
    </div>
  );
}

/** Position a fixed popover under its anchor, inside the viewport. */
function useAnchoredBox(anchor: React.RefObject<HTMLElement | null>, width: number, align: "left" | "right") {
  const place = () => {
    const rect = anchor.current?.getBoundingClientRect();
    if (!rect) return { top: 0, left: 0 };
    const left = align === "left" ? rect.left : rect.right - width;
    return {
      top: rect.bottom + 6,
      left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
    };
  };
  const [box, setBox] = useState(place);
  useLayoutEffect(() => {
    const update = () => setBox(place());
    update();
    addEventListener("resize", update);
    addEventListener("scroll", update, true);
    return () => {
      removeEventListener("resize", update);
      removeEventListener("scroll", update, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, width, align]);
  return box;
}

/** Close on Escape or on any press outside `inside` (and the trigger). */
function useDismiss(open: boolean, refs: React.RefObject<HTMLElement | null>[], close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (event: Event) => {
      const target = event.target as Node | null;
      if (target && refs.some((ref) => ref.current?.contains(target))) return;
      close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, close]);
}

const POPOVER: React.CSSProperties = {
  position: "fixed",
  zIndex: 80,
  background: H.white,
  border: `1px solid ${H.edgeStrong}`,
  borderRadius: 14,
  boxShadow: `0 24px 60px ${H.popShadow}`,
  overflow: "hidden",
  textAlign: "left",
  color: H.ink,
};

/**
 * Home's "Find a project": the compact, fully rounded search pill and the
 * 520px dropdown it opens.
 */
export function ProjectSearch({
  projects,
  activeSiteId,
  onSelect,
}: {
  projects: HomePickerProject[];
  activeSiteId: string | null;
  onSelect: (siteId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const [sort, setSort] = useState<PickerSort>("recent");
  const pill = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [pins] = usePins();
  const { flat } = pickerRows(projects, pins, query, sort);

  const close = () => {
    setOpen(false);
    setQuery("");
    setHi(0);
    input.current?.blur();
  };
  useDismiss(open, [pill, panel], close);

  const pick = (siteId: string) => {
    close();
    onSelect(siteId);
  };

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={pill}
        data-testid="home-project-search"
        onClick={() => input.current?.focus()}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: 260,
          height: 34,
          padding: "0 10px 0 14px",
          borderRadius: 999,
          background: H.white,
          border: `1px solid ${open ? H.ink : H.edgeStrong}`,
          boxShadow: open ? `0 0 0 3px ${H.focusRing}` : `0 6px 18px ${H.pillShadow}`,
          cursor: "text",
        }}
      >
        <Glyph d={ICON.search} size={13} color={H.faint} />
        <input
          ref={input}
          data-testid="home-project-search-input"
          aria-label="Find a project"
          value={query}
          placeholder="Find a project"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setHi(0);
            setOpen(true);
          }}
          onKeyDown={(event) => pickerKey(event, flat, hi, setHi, pick, close)}
          style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent", font: "inherit", fontSize: 13, color: H.ink }}
        />
        {open ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              close();
            }}
            aria-label="Close"
            style={{ width: 26, height: 18, borderRadius: 4, border: `1px solid ${H.edgeStrong}`, background: "transparent", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_MONO, fontSize: 8, color: H.muted, cursor: "pointer", padding: 0 }}
          >
            ESC
          </button>
        ) : (
          <span title="⌘K searches every project, walk and record" style={{ fontFamily: FONT_MONO, fontSize: 8.5, color: H.faint, border: `1px solid ${H.edgeStrong}`, borderRadius: 4, padding: "1px 4px" }}>⌘K</span>
        )}
      </div>
      {open && (
        <div
          ref={panel}
          role="menu"
          aria-label="Find a project"
          data-testid="home-project-search-menu"
          className="pop"
          style={{ ...POPOVER, position: "absolute", right: 0, top: 40, width: 520, zIndex: 40 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "8px 10px 4px" }}>
            <span style={{ ...MONO_HEAD, letterSpacing: "1.2px", marginRight: 6 }}>SORT</span>
            {(
              [
                ["recent", "Recent"],
                ["attention", "Needs attention"],
                ["az", "A–Z"],
              ] as const
            ).map(([id, text]) => (
              <button
                key={id}
                type="button"
                data-testid={`project-sort-${id}`}
                aria-pressed={sort === id}
                onClick={() => {
                  setSort(id);
                  setHi(0);
                }}
                style={{
                  height: 26,
                  padding: "0 10px",
                  borderRadius: 6,
                  border: 0,
                  background: sort === id ? H.white : "transparent",
                  boxShadow: sort === id ? `0 1px 3px ${H.edgeStrong}` : "none",
                  font: "inherit",
                  fontSize: 12,
                  fontWeight: 600,
                  color: sort === id ? H.ink : H.sub,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {text}
              </button>
            ))}
          </div>
          <ProjectRows
            projects={projects}
            query={query}
            sort={sort}
            hi={hi}
            setHi={setHi}
            activeSiteId={activeSiteId}
            onPick={pick}
            detailed
            maxHeight={360}
          />
        </div>
      )}
    </div>
  );
}
